import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { gsap } from "gsap";

interface TickerContextType {
  tickerAnimation: React.MutableRefObject<gsap.core.Timeline | null>;
  isTickerInitialized: boolean;
  setTickerInitialized: (value: boolean) => void;
  resetTicker: () => void;
}

const TickerContext = createContext<TickerContextType | undefined>(undefined);

export const TickerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const tickerAnimation = useRef<gsap.core.Timeline | null>(null);
  const [isTickerInitialized, setTickerInitialized] = useState(false);

  // Reset function to handle page refresh and cleanup
  const resetTicker = () => {
    if (tickerAnimation.current) {
      tickerAnimation.current.kill();
      tickerAnimation.current = null;
    }
    setTickerInitialized(false);
  };

  // Handle visibility changes (page refresh, tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause animation when tab is not visible
        if (tickerAnimation.current) {
          tickerAnimation.current.pause();
        }
      } else {
        // Resume animation when tab becomes visible
        if (tickerAnimation.current && isTickerInitialized) {
          tickerAnimation.current.play();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isTickerInitialized]);

  // Handle page refresh/reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      resetTicker();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return (
    <TickerContext.Provider
      value={{
        tickerAnimation,
        isTickerInitialized,
        setTickerInitialized,
        resetTicker,
      }}
    >
      {children}
    </TickerContext.Provider>
  );
};

export const useTickerContext = () => {
  const context = useContext(TickerContext);
  if (context === undefined) {
    throw new Error("useTickerContext must be used within a TickerProvider");
  }
  return context;
};
